import { supabase } from "~/integrations/supabase/client";
import type {
  AccountMappingTemplate,
  AccountMapping,
  MappingTemplateFilters,
  MappingTemplateFormData,
  AccountMappingFilters,
  AccountMappingFormData,
} from "~/types/contabilidad";

export interface MappingImportData {
  account_code: string;
  reporte: string;
  clase: string;
  orden_clase?: number;
  orden_tipo?: number;
  tipo?: string;
  sub_clase: string;
  orden_subclase?: number;
  sub_clase_2: string;
  orden_subclase_2?: number;
  grupo_cuenta_code?: string;
  orden_grupo_cuenta?: number;
  clave?: number;
  signo?: number;
  clase_1?: string;
}

export interface MappingImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ account_code: string; error: string }>;
}

export const accountMappingService = {
  // ============================================
  // TEMPLATE OPERATIONS
  // ============================================

  async getTemplates(
    filters?: MappingTemplateFilters
  ): Promise<AccountMappingTemplate[]> {
    let query = supabase
      .from("account_mapping_template")
      .select(
        `
        *,
        company:company_id(*)
      `
      )
      .eq("is_deleted", false);

    if (filters?.company_id) {
      query = query.eq("company_id", filters.company_id);
    }

    if (filters?.is_active !== undefined) {
      query = query.eq("is_active", filters.is_active);
    }

    if (filters?.search) {
      query = query.or(
        `name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`
      );
    }

    const { data, error } = await query.order("name", { ascending: true });

    if (error) throw error;

    // Get mapping counts for each template
    const templateIds = (data || []).map((t) => t.id);
    if (templateIds.length > 0) {
      const { data: countData } = await supabase
        .from("account_mapping")
        .select("template_id")
        .in("template_id", templateIds)
        .eq("is_deleted", false);

      const countMap = new Map<string, number>();
      (countData || []).forEach((m) => {
        countMap.set(m.template_id, (countMap.get(m.template_id) || 0) + 1);
      });

      return (data || []).map((t) => ({
        ...t,
        mappings_count: countMap.get(t.id) || 0,
      })) as AccountMappingTemplate[];
    }

    return data as AccountMappingTemplate[];
  },

  async getTemplateById(id: string): Promise<AccountMappingTemplate> {
    const { data, error } = await supabase
      .from("account_mapping_template")
      .select("*, company:company_id(*)")
      .eq("id", id)
      .eq("is_deleted", false)
      .single();

    if (error) throw error;
    return data as AccountMappingTemplate;
  },

  async createTemplate(
    formData: MappingTemplateFormData
  ): Promise<AccountMappingTemplate> {
    const { data, error } = await supabase
      .from("account_mapping_template")
      .insert({
        ...formData,
        is_active: formData.is_active ?? true,
      })
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as AccountMappingTemplate;
  },

  async updateTemplate(
    id: string,
    formData: Partial<MappingTemplateFormData>
  ): Promise<AccountMappingTemplate> {
    const { data, error } = await supabase
      .from("account_mapping_template")
      .update(formData)
      .eq("id", id)
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as AccountMappingTemplate;
  },

  async deleteTemplate(id: string): Promise<void> {
    // Soft delete template (cascades to mappings via DB)
    const { error } = await supabase
      .from("account_mapping_template")
      .update({ is_deleted: true })
      .eq("id", id);

    if (error) throw error;
  },

  async toggleTemplateActive(
    id: string,
    isActive: boolean
  ): Promise<AccountMappingTemplate> {
    const { data, error } = await supabase
      .from("account_mapping_template")
      .update({ is_active: isActive })
      .eq("id", id)
      .select("*, company:company_id(*)")
      .single();

    if (error) throw error;
    return data as AccountMappingTemplate;
  },

  // ============================================
  // MAPPING OPERATIONS
  // ============================================

  async getMappings(filters?: AccountMappingFilters): Promise<AccountMapping[]> {
    let query = supabase
      .from("account_mapping")
      .select(
        `
        *,
        account:account_id(*),
        grupo_cuenta:grupo_cuenta_id(*),
        template:template_id(*)
      `
      )
      .eq("is_deleted", false);

    if (filters?.template_id) {
      query = query.eq("template_id", filters.template_id);
    }

    if (filters?.reporte) {
      query = query.eq("reporte", filters.reporte);
    }

    if (filters?.clase) {
      query = query.eq("clase", filters.clase);
    }

    if (filters?.grupo_cuenta_id) {
      query = query.eq("grupo_cuenta_id", filters.grupo_cuenta_id);
    }

    const { data, error } = await query.order("orden_clase", { ascending: true });

    if (error) throw error;

    // Filter by search if provided (account code/name)
    let results = data as AccountMapping[];
    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      results = results.filter(
        (m) =>
          m.account?.code.toLowerCase().includes(searchLower) ||
          m.account?.name.toLowerCase().includes(searchLower)
      );
    }

    return results;
  },

  async getMappingById(id: string): Promise<AccountMapping> {
    const { data, error } = await supabase
      .from("account_mapping")
      .select(
        `
        *,
        account:account_id(*),
        grupo_cuenta:grupo_cuenta_id(*),
        template:template_id(*)
      `
      )
      .eq("id", id)
      .eq("is_deleted", false)
      .single();

    if (error) throw error;
    return data as AccountMapping;
  },

  async createMapping(formData: AccountMappingFormData): Promise<AccountMapping> {
    const { data, error } = await supabase
      .from("account_mapping")
      .insert({
        ...formData,
        orden_clase: formData.orden_clase ?? 0,
        orden_tipo: formData.orden_tipo ?? 0,
        orden_subclase: formData.orden_subclase ?? 0,
        orden_subclase_2: formData.orden_subclase_2 ?? 0,
        orden_grupo_cuenta: formData.orden_grupo_cuenta ?? 0,
      })
      .select(
        `
        *,
        account:account_id(*),
        grupo_cuenta:grupo_cuenta_id(*),
        template:template_id(*)
      `
      )
      .single();

    if (error) throw error;
    return data as AccountMapping;
  },

  async updateMapping(
    id: string,
    formData: Partial<AccountMappingFormData>
  ): Promise<AccountMapping> {
    const { data, error } = await supabase
      .from("account_mapping")
      .update(formData)
      .eq("id", id)
      .select(
        `
        *,
        account:account_id(*),
        grupo_cuenta:grupo_cuenta_id(*),
        template:template_id(*)
      `
      )
      .single();

    if (error) throw error;
    return data as AccountMapping;
  },

  async deleteMapping(id: string): Promise<void> {
    // Soft delete
    const { error } = await supabase
      .from("account_mapping")
      .update({ is_deleted: true })
      .eq("id", id);

    if (error) throw error;
  },

  /**
   * Bulk update multiple mappings at once
   */
  async bulkUpdateMappings(
    updates: Array<{ id: string; data: Partial<AccountMappingFormData> }>
  ): Promise<{ updated: number; errors: Array<{ id: string; error: string }> }> {
    const result = { updated: 0, errors: [] as Array<{ id: string; error: string }> };

    for (const update of updates) {
      const { error } = await supabase
        .from("account_mapping")
        .update(update.data)
        .eq("id", update.id);

      if (error) {
        result.errors.push({ id: update.id, error: error.message });
      } else {
        result.updated++;
      }
    }

    return result;
  },

  /**
   * Import mappings from Excel data
   * Creates new mappings or updates existing ones for the same account
   */
  async importMappings(
    templateId: string,
    companyId: string,
    mappings: MappingImportData[]
  ): Promise<MappingImportResult> {
    const result: MappingImportResult = {
      created: 0,
      updated: 0,
      skipped: 0,
      errors: [],
    };

    if (mappings.length === 0) {
      return result;
    }

    // Get all accounts for this company
    const { data: accounts, error: accountsError } = await supabase
      .from("account")
      .select("id, code")
      .eq("company_id", companyId)
      .eq("is_deleted", false);

    if (accountsError) throw accountsError;

    const accountCodeToId = new Map(
      (accounts || []).map((a) => [a.code.toLowerCase(), a.id])
    );

    // Get classification groups for this company
    const { data: groups, error: groupsError } = await supabase
      .from("account_classification_group")
      .select("id, code")
      .eq("company_id", companyId)
      .eq("is_deleted", false);

    if (groupsError) throw groupsError;

    const groupCodeToId = new Map(
      (groups || []).map((g) => [g.code.toLowerCase(), g.id])
    );

    // Get existing mappings for this template
    const { data: existingMappings, error: mappingsError } = await supabase
      .from("account_mapping")
      .select("id, account_id")
      .eq("template_id", templateId)
      .eq("is_deleted", false);

    if (mappingsError) throw mappingsError;

    const accountToMappingId = new Map(
      (existingMappings || []).map((m) => [m.account_id, m.id])
    );

    // Process each mapping
    for (const mapping of mappings) {
      const accountId = accountCodeToId.get(mapping.account_code.toLowerCase());
      if (!accountId) {
        result.errors.push({
          account_code: mapping.account_code,
          error: `Cuenta no encontrada: ${mapping.account_code}`,
        });
        continue;
      }

      // Validate reporte
      if (mapping.reporte !== "Balance" && mapping.reporte !== "Resultados") {
        result.errors.push({
          account_code: mapping.account_code,
          error: `Reporte inválido: ${mapping.reporte}. Debe ser "Balance" o "Resultados"`,
        });
        continue;
      }

      // Validate clase
      const validClases = [
        "Activo",
        "Pasivo y Patrimonio",
        "Costos",
        "Gastos",
        "Ingresos",
      ];
      if (!validClases.includes(mapping.clase)) {
        result.errors.push({
          account_code: mapping.account_code,
          error: `Clase inválida: ${mapping.clase}`,
        });
        continue;
      }

      // Get grupo_cuenta_id if provided
      let grupoId: string | null = null;
      if (mapping.grupo_cuenta_code) {
        grupoId =
          groupCodeToId.get(mapping.grupo_cuenta_code.toLowerCase()) || null;
      }

      const mappingData = {
        template_id: templateId,
        account_id: accountId,
        reporte: mapping.reporte as "Balance" | "Resultados",
        clase: mapping.clase,
        orden_clase: mapping.orden_clase ?? 0,
        orden_tipo: mapping.orden_tipo ?? 0,
        tipo: mapping.tipo ?? null,
        sub_clase: mapping.sub_clase,
        orden_subclase: mapping.orden_subclase ?? 0,
        sub_clase_2: mapping.sub_clase_2,
        orden_subclase_2: mapping.orden_subclase_2 ?? 0,
        grupo_cuenta_id: grupoId,
        orden_grupo_cuenta: mapping.orden_grupo_cuenta ?? 0,
        clave: mapping.clave ?? null,
        signo: mapping.signo ?? null,
        clase_1: mapping.clase_1 ?? null,
      };

      const existingMappingId = accountToMappingId.get(accountId);

      if (existingMappingId) {
        // Update existing mapping
        const { error } = await supabase
          .from("account_mapping")
          .update(mappingData)
          .eq("id", existingMappingId);

        if (error) {
          result.errors.push({
            account_code: mapping.account_code,
            error: error.message,
          });
        } else {
          result.updated++;
        }
      } else {
        // Create new mapping
        const { error } = await supabase
          .from("account_mapping")
          .insert(mappingData);

        if (error) {
          result.errors.push({
            account_code: mapping.account_code,
            error: error.message,
          });
        } else {
          result.created++;
        }
      }
    }

    return result;
  },

  /**
   * Copy a template with all its mappings
   */
  async copyTemplate(
    sourceTemplateId: string,
    newName: string,
    companyId: string
  ): Promise<AccountMappingTemplate> {
    // Get source template
    const sourceTemplate = await this.getTemplateById(sourceTemplateId);

    // Create new template
    const newTemplate = await this.createTemplate({
      company_id: companyId,
      name: newName,
      description: sourceTemplate.description || undefined,
      is_active: true,
    });

    // Get all mappings from source template
    const sourceMappings = await this.getMappings({
      template_id: sourceTemplateId,
    });

    // Copy mappings to new template
    if (sourceMappings.length > 0) {
      const newMappings = sourceMappings.map((m) => ({
        template_id: newTemplate.id,
        account_id: m.account_id,
        reporte: m.reporte,
        clase: m.clase,
        orden_clase: m.orden_clase,
        orden_tipo: m.orden_tipo,
        tipo: m.tipo,
        sub_clase: m.sub_clase,
        orden_subclase: m.orden_subclase,
        sub_clase_2: m.sub_clase_2,
        orden_subclase_2: m.orden_subclase_2,
        grupo_cuenta_id: m.grupo_cuenta_id,
        orden_grupo_cuenta: m.orden_grupo_cuenta,
        clave: m.clave,
        signo: m.signo,
        clase_1: m.clase_1,
      }));

      const { error } = await supabase
        .from("account_mapping")
        .insert(newMappings);

      if (error) throw error;
    }

    return newTemplate;
  },
};
